import type { CSSProperties } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'

const DEFAULT_TRACK = '#e5e7eb'

export function clampPercent(n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(100, Math.round(n)))
}

export interface PercentDonutProps {
  /** 0–100; values are clamped and rounded for the chart segments. */
  percent: number
  /** Fill for the progress arc */
  color?: string
  /** Fill for the remainder (track) */
  trackColor?: string
  /** Pixel size of the square chart area */
  size: number
  innerRadius: number
  outerRadius: number
  className?: string
  style?: CSSProperties
  /** Recharts pie animation (default off for stable SSR/layout) */
  isAnimationActive?: boolean
}

/**
 * Donut chart from a single percentage: two slices `p` and `100 − p`.
 * Recharts divides the full ring proportionally.
 */
export function PercentDonut({
  percent,
  color = '#10b981',
  trackColor = DEFAULT_TRACK,
  size,
  innerRadius,
  outerRadius,
  className = '',
  style,
  isAnimationActive = false,
}: PercentDonutProps) {
  const p = clampPercent(percent)
  const data = [{ value: p }, { value: 100 - p }]

  return (
    <div className={className} style={{ width: size, height: size, minWidth: size, minHeight: size, ...style }}>
      <ResponsiveContainer width={size} height={size}>
        <PieChart>
          <Pie
            data={data}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            dataKey="value"
            startAngle={90}
            endAngle={-270}
            isAnimationActive={isAnimationActive}
          >
            <Cell fill={color} />
            <Cell fill={trackColor} />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

export default PercentDonut
